import React, { useState } from "react";
import { toast } from "sonner";
import { sharedApi } from "@/lib/api";
import { Image as ImageIcon, X, Loader2, Star } from "lucide-react";

const MAX_MB = 3;
const MAX_BYTES = MAX_MB * 1024 * 1024;
const MIME_RE = /^image\/(png|jpe?g|gif)$/i;

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/**
 * MultiImageUploader — thumb + up to N other images.
 *
 * Spec (iter-215 Post-Deal): the first image is the required thumb; up to
 * `max` total images (default 8), ≤3 MB each, png/jpeg/gif. Backend contract:
 * `POST /api/uploads/image { data: <base64> }` → `{ url: "https://…" }`.
 *
 * Props:
 *   images  — string[] URLs; `images[0]` is treated as the thumb.
 *   onChange(images) — fired whenever the list changes.
 *   testId  — prefix for data-testids.
 *   max     — hard cap (default 8).
 */
export default function MultiImageUploader({
  images = [],
  onChange,
  testId = "multi-image",
  max = 8,
}) {
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!MIME_RE.test(file.type)) {
      toast.error("Only PNG, JPG, or GIF allowed.");
      return null;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Each image must be ≤ ${MAX_MB} MB.`);
      return null;
    }
    const dataUri = await readAsDataURL(file);
    const { data } = await sharedApi.post("/uploads/image", { data: dataUri });
    return data?.url || null;
  };

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const remaining = Math.max(0, max - images.length);
      const picked = Array.from(files).slice(0, remaining);
      if (files.length > remaining) {
        toast.warning(`Only ${remaining} more image(s) allowed — extras skipped.`);
      }
      const uploaded = [];
      for (const f of picked) {
        // Sequential upload to keep progress obvious + avoid overwhelming the
        // shared /uploads endpoint on slow connections.
        const url = await upload(f);
        if (url) uploaded.push(url);
      }
      if (uploaded.length) onChange([...images, ...uploaded]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (idx) => onChange(images.filter((_, i) => i !== idx));
  const makeThumb = (idx) => {
    if (idx === 0) return;
    const next = [images[idx], ...images.filter((_, i) => i !== idx)];
    onChange(next);
  };

  const canAdd = images.length < max;

  return (
    <div data-testid={`${testId}-wrap`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {images.map((url, i) => (
          <div
            key={url + i}
            data-testid={`${testId}-item-${i}`}
            className={`relative aspect-square rounded-xl overflow-hidden border-2 ${
              i === 0 ? "border-[color:var(--brand-orange)]" : "border-soft"
            }`}
          >
            <img src={url} alt={`Image ${i + 1}`} className="w-full h-full object-cover" />
            {i === 0 && (
              <div
                className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 rounded-full bg-[color:var(--brand-orange)] text-white text-[10px] font-bold px-2 py-0.5"
                data-testid={`${testId}-thumb-badge`}
              >
                <Star className="w-3 h-3" fill="currentColor" /> Thumb
              </div>
            )}
            <button
              type="button"
              onClick={() => removeAt(i)}
              data-testid={`${testId}-remove-${i}`}
              aria-label={`Remove image ${i + 1}`}
              className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-slate-900/70 hover:bg-slate-900 text-white grid place-items-center"
            >
              <X className="w-3 h-3" />
            </button>
            {i !== 0 && (
              <button
                type="button"
                onClick={() => makeThumb(i)}
                data-testid={`${testId}-make-thumb-${i}`}
                className="absolute bottom-1.5 left-1.5 rounded-full bg-white/90 hover:bg-white text-slate-800 text-[10px] font-bold px-2 py-0.5"
              >
                Set as thumb
              </button>
            )}
          </div>
        ))}

        {canAdd && (
          <label
            className={`aspect-square rounded-xl border-2 border-dashed cursor-pointer flex flex-col items-center justify-center gap-1.5 text-slate-500 hover:text-[color:var(--brand-blue)] hover:border-[color:var(--brand-blue)] transition-colors ${
              uploading ? "opacity-60" : ""
            } ${images.length === 0 ? "border-[color:var(--brand-orange)]/60 bg-orange-50/40" : "border-soft bg-white"}`}
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <ImageIcon className="w-5 h-5" />
                <span className="text-[11px] font-semibold text-center px-2">
                  {images.length === 0 ? "Add thumb image *" : `Add image (${images.length}/${max})`}
                </span>
              </>
            )}
            <input
              type="file"
              accept="image/png,image/jpeg,image/gif"
              multiple
              className="hidden"
              data-testid={`${testId}-input`}
              onChange={(e) => handleFiles(e.target.files)}
            />
          </label>
        )}
      </div>
      <div className="mt-2 text-[11px] text-slate-400">
        PNG, JPG, GIF · ≤ {MAX_MB} MB each · up to {max} images · first image is the thumb
      </div>
    </div>
  );
}
